import { useEffect, useState } from "react";
import { ApiValidationError } from "./api";
import { ConditionSection } from "./ConditionSection";
import { ConfirmDialog } from "./ConfirmDialog";
import { RuleEditor } from "./RuleEditor";
import type {
    ActionRule,
    EditorMetadata,
    Transition,
    TransitionAction,
    TransitionType,
} from "./types";
import type { EditorMutations } from "./useEditorMutations";

/**
 * What deleting a transition actually destroys.
 *
 * Conditions are **not** in this list, and that is not an oversight. ActionRule.condition is a
 * foreign key *to* Condition with on_delete=CASCADE, so the cascade runs the other way: deleting
 * a condition deletes the rules that point at it, while deleting a rule leaves its condition
 * behind. Transition.condition is SET_NULL, so that one survives too. Saying "3 conditions will
 * be deleted" would simply be false — see orphanedConditionCount for what is really left.
 */
export function transitionConsequences(transition: Transition): string[] {
    const actions = transition.actionConfigs.length;
    const rules = transition.actionConfigs.reduce(
        (total, config) => total + config.rules.length,
        0,
    );

    const consequences: string[] = [];
    if (actions) {
        consequences.push(
            `${actions} configured ${actions === 1 ? "action" : "actions"}`,
        );
    }
    if (rules) {
        consequences.push(`${rules} ${rules === 1 ? "rule" : "rules"}`);
    }
    return consequences;
}

/** What deleting a configured action actually destroys. Its rules, not their conditions. */
export function actionConsequences(config: TransitionAction): string[] {
    const rules = config.rules.length;
    return rules ? [`${rules} ${rules === 1 ? "rule" : "rules"}`] : [];
}

/**
 * Conditions that would be left with nothing pointing at them.
 *
 * They are not deleted and they are not harmless: they stay attached to the definition and keep
 * showing up in the admin's condition list, so the dialog says so rather than implying a tidy
 * cascade.
 */
export function orphanedConditionCount(
    subject: Transition | TransitionAction,
): number {
    const configs =
        "actionConfigs" in subject ? subject.actionConfigs : [subject];
    const ruleConditions = configs.reduce(
        (total, config) =>
            total + config.rules.filter((rule) => rule.condition).length,
        0,
    );
    const own = "actionConfigs" in subject && subject.condition ? 1 : 0;
    return ruleConditions + own;
}

/** The dialog's note about orphans, or null when there are none. */
export function orphanNote(
    subject: Transition | TransitionAction,
): string | null {
    const count = orphanedConditionCount(subject);
    if (!count) {
        return null;
    }
    return `${count} ${
        count === 1 ? "condition" : "conditions"
    } will be left unused rather than deleted, and will still appear in the admin.`;
}

type PendingDelete =
    | { kind: "transition"; transition: Transition }
    | { kind: "action"; config: TransitionAction }
    | { kind: "rule"; rule: ActionRule; index: number };

function ActionEditor({
    config,
    index,
    actionCount,
    transition,
    metadata,
    mutations,
    onDirtyChange,
    onRequestDelete,
}: {
    config: TransitionAction;
    index: number;
    actionCount: number;
    transition: Transition;
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDirtyChange: (key: string, dirty: boolean) => void;
    onRequestDelete: (pending: PendingDelete) => void;
}) {
    const move = (offset: number) => {
        const ids = transition.actionConfigs.map((item) => item.id);
        const target = index + offset;
        [ids[index], ids[target]] = [ids[target], ids[index]];
        mutations.moveActions.mutate({
            transitionId: transition.id,
            actionIds: ids,
        });
    };

    return (
        <li className="aer-wf-action">
            <div className="aer-wf-action__header">
                <h4 className="aer-wf-action__title">{config.title}</h4>
                {!config.isRegistered && (
                    <span className="aer-wf-badge aer-wf-badge--danger">
                        not registered — the engine skips this
                    </span>
                )}
                <div className="aer-wf-rule__controls">
                    <button
                        type="button"
                        className="aer-wf-icon-button"
                        aria-label={`Run ${config.title} earlier`}
                        disabled={
                            index === 0 || mutations.moveActions.isLoading
                        }
                        onClick={() => move(-1)}
                    >
                        ↑
                    </button>
                    <button
                        type="button"
                        className="aer-wf-icon-button"
                        aria-label={`Run ${config.title} later`}
                        disabled={
                            index === actionCount - 1 ||
                            mutations.moveActions.isLoading
                        }
                        onClick={() => move(1)}
                    >
                        ↓
                    </button>
                    <button
                        type="button"
                        className="aer-wf-icon-button aer-wf-icon-button--danger"
                        aria-label={`Remove ${config.title}`}
                        onClick={() =>
                            onRequestDelete({ kind: "action", config })
                        }
                    >
                        ✕
                    </button>
                </div>
            </div>

            <p className="aer-wf-action__name">{config.actionName}</p>

            {config.rules.length > 0 && (
                <p className="aer-wf-action__hint">
                    The first matching rule decides which parameters are used.
                </p>
            )}

            <ol className="aer-wf-rules">
                {config.rules.map((rule, ruleIndex) => (
                    <RuleEditor
                        key={rule.id}
                        rule={rule}
                        index={ruleIndex}
                        ruleCount={config.rules.length}
                        config={config}
                        metadata={metadata}
                        mutations={mutations}
                        onDirtyChange={onDirtyChange}
                        onRequestDelete={() =>
                            onRequestDelete({
                                kind: "rule",
                                rule,
                                index: ruleIndex,
                            })
                        }
                    />
                ))}
            </ol>

            <button
                type="button"
                className="aer-wf-button aer-wf-button--secondary"
                disabled={mutations.addRule.isLoading}
                onClick={() => mutations.addRule.mutate(config.id)}
            >
                Add rule
            </button>
        </li>
    );
}

export function TransitionEditor({
    transition,
    metadata,
    mutations,
    onDirtyChange,
    onDeleted,
}: {
    transition: Transition;
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDirtyChange: (key: string, dirty: boolean) => void;
    onDeleted: () => void;
}) {
    const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
        null,
    );
    const [draftType, setDraftType] = useState<TransitionType>(
        transition.transitionType,
    );
    const [draftPriority, setDraftPriority] = useState(
        String(transition.priority),
    );
    const [actionToAdd, setActionToAdd] = useState("");

    useEffect(() => {
        setDraftType(transition.transitionType);
        setDraftPriority(String(transition.priority));
    }, [transition.transitionType, transition.priority]);

    const isDirty =
        draftType !== transition.transitionType ||
        draftPriority !== String(transition.priority);

    const dirtyKey = `transition:${transition.id}`;
    useEffect(() => {
        onDirtyChange(dirtyKey, isDirty);
        return () => onDirtyChange(dirtyKey, false);
    }, [dirtyKey, isDirty, onDirtyChange]);

    const save = mutations.saveTransition;
    const fieldErrors =
        save.error instanceof ApiValidationError ? save.error.fieldErrors : {};

    const configuredNames = new Set(
        transition.actionConfigs.map((config) => config.actionName),
    );
    const addableActions = metadata.availableActions.filter(
        (option) => !configuredNames.has(option.value),
    );

    const confirmDelete = () => {
        if (!pendingDelete) {
            return;
        }
        if (pendingDelete.kind === "transition") {
            mutations.removeTransition.mutate(pendingDelete.transition.id, {
                onSuccess: () => {
                    setPendingDelete(null);
                    onDeleted();
                },
            });
            return;
        }
        if (pendingDelete.kind === "action") {
            mutations.removeAction.mutate(pendingDelete.config.id, {
                onSuccess: () => setPendingDelete(null),
            });
            return;
        }
        mutations.removeRule.mutate(pendingDelete.rule.id, {
            onSuccess: () => setPendingDelete(null),
        });
    };

    const dialogFor = (pending: PendingDelete) => {
        if (pending.kind === "transition") {
            return {
                title: `Delete the ${pending.transition.fromStatus} → ${pending.transition.toStatus} transition?`,
                consequences: transitionConsequences(pending.transition),
                note: orphanNote(pending.transition),
                confirmLabel: "Delete transition",
            };
        }
        if (pending.kind === "action") {
            return {
                title: `Remove ${pending.config.title} from this transition?`,
                consequences: actionConsequences(pending.config),
                note: orphanNote(pending.config),
                confirmLabel: "Remove action",
            };
        }
        return {
            title: `Delete rule ${pending.index + 1}?`,
            consequences: [],
            // The rule's condition is not deleted with it — the cascade runs the other way.
            note: pending.rule.condition
                ? `The condition "${pending.rule.condition.name}" will be left unused rather than deleted.`
                : null,
            confirmLabel: "Delete rule",
        };
    };

    return (
        <aside className="aer-wf-panel" aria-label="Transition detail">
            <header className="aer-wf-panel__header">
                <h3>
                    {transition.fromStatus} → {transition.toStatus}
                </h3>
                <button
                    type="button"
                    className="aer-wf-button aer-wf-button--danger"
                    onClick={() =>
                        setPendingDelete({ kind: "transition", transition })
                    }
                >
                    Delete transition
                </button>
            </header>

            <section>
                <h4>Transition</h4>
                <div className="aer-wf-fields">
                    <div className="aer-wf-field">
                        <label htmlFor="aer-wf-type">Type</label>
                        <select
                            id="aer-wf-type"
                            value={draftType}
                            onChange={(event) =>
                                setDraftType(
                                    event.target.value as TransitionType,
                                )
                            }
                        >
                            {metadata.transitionTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                        {fieldErrors.transition_type && (
                            <p className="aer-wf-field__error" role="alert">
                                {fieldErrors.transition_type.join(" ")}
                            </p>
                        )}
                        {draftType === "MANUAL" && transition.condition && (
                            <p className="aer-wf-field__hint aer-wf-field__hint--warn">
                                A manual transition cannot keep a condition —
                                remove the condition first.
                            </p>
                        )}
                    </div>

                    <div className="aer-wf-field">
                        <label htmlFor="aer-wf-priority">Priority</label>
                        <input
                            id="aer-wf-priority"
                            type="number"
                            min="0"
                            value={draftPriority}
                            onChange={(event) =>
                                setDraftPriority(event.target.value)
                            }
                        />
                        <p className="aer-wf-field__hint">
                            Higher wins when more than one transition leaves
                            this status.
                        </p>
                        {fieldErrors.priority && (
                            <p className="aer-wf-field__error" role="alert">
                                {fieldErrors.priority.join(" ")}
                            </p>
                        )}
                    </div>
                </div>

                {fieldErrors.to_status && (
                    <p className="aer-wf-field__error" role="alert">
                        {fieldErrors.to_status.join(" ")}
                    </p>
                )}

                <div className="aer-wf-rule__save">
                    <button
                        type="button"
                        className="aer-wf-button"
                        disabled={!isDirty || save.isLoading}
                        onClick={() =>
                            save.mutate({
                                id: transition.id,
                                fields: {
                                    transitionType: draftType,
                                    priority: Number(draftPriority) || 0,
                                },
                            })
                        }
                    >
                        {save.isLoading ? "Saving…" : "Save transition"}
                    </button>
                    {isDirty && (
                        <span className="aer-wf-rule__dirty">Unsaved</span>
                    )}
                </div>
            </section>

            <section>
                <h4>Condition</h4>
                {/* A manual transition's condition is never evaluated, so offering to build one
                    would be offering a guard that does not apply. The API rejects it too. */}
                {draftType === "MANUAL" && !transition.condition ? (
                    <p className="aer-wf-panel__empty">
                        Manual transitions are fired by a person, so a condition
                        on one would never be evaluated.
                    </p>
                ) : (
                    <ConditionSection
                        condition={transition.condition}
                        attachTo={{ kind: "transition", id: transition.id }}
                        metadata={metadata}
                        mutations={mutations}
                        dirtyKey={`condition:${transition.id}`}
                        onDirtyChange={onDirtyChange}
                        absentMeaning="None — this transition fires whenever it is reached."
                    />
                )}
            </section>

            <section>
                <h4>Actions</h4>
                {transition.actionConfigs.length ? (
                    <ol className="aer-wf-actions">
                        {transition.actionConfigs.map((config, index) => (
                            <ActionEditor
                                key={config.id}
                                config={config}
                                index={index}
                                actionCount={transition.actionConfigs.length}
                                transition={transition}
                                metadata={metadata}
                                mutations={mutations}
                                onDirtyChange={onDirtyChange}
                                onRequestDelete={setPendingDelete}
                            />
                        ))}
                    </ol>
                ) : (
                    <p className="aer-wf-panel__empty">
                        None configured on this transition.
                    </p>
                )}

                <div className="aer-wf-add-action">
                    <label htmlFor="aer-wf-add-action">Add an action</label>
                    <select
                        id="aer-wf-add-action"
                        value={actionToAdd}
                        onChange={(event) => setActionToAdd(event.target.value)}
                    >
                        <option value="">— choose —</option>
                        {addableActions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="aer-wf-button"
                        disabled={!actionToAdd || mutations.addAction.isLoading}
                        onClick={() => {
                            mutations.addAction.mutate(
                                {
                                    transitionId: transition.id,
                                    actionName: actionToAdd,
                                },
                                { onSuccess: () => setActionToAdd("") },
                            );
                        }}
                    >
                        Add
                    </button>
                    {!addableActions.length && (
                        <p className="aer-wf-field__hint">
                            Every action available to this company is already
                            configured here.
                        </p>
                    )}
                    {mutations.addAction.error instanceof
                        ApiValidationError && (
                        <p className="aer-wf-field__error" role="alert">
                            {mutations.addAction.error.message}
                        </p>
                    )}
                </div>
            </section>

            {pendingDelete && (
                <ConfirmDialog
                    {...dialogFor(pendingDelete)}
                    isPending={
                        mutations.removeTransition.isLoading ||
                        mutations.removeAction.isLoading ||
                        mutations.removeRule.isLoading
                    }
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDelete(null)}
                />
            )}
        </aside>
    );
}
